// The recorded parallel-test decision (issue #554, Part B.3) and its human doc mirror.
//
// One code path derives the machine record (`commands.test_parallel` + the `testing` block) from a
// pack runner and the project's lockfile, and the same path is called at onboarding and at
// `checks run`. A second function mirrors the parallel command into the stack doc's command table
// so a human can see what paqad recorded, without paqad ever asking the developer to configure it.

import { createHash } from 'node:crypto';
import { existsSync, readdirSync, readFileSync, writeFileSync } from 'node:fs';
import { dirname, join } from 'node:path';

import { getPacksForFrameworks } from '@/packs/project-packs.js';
import { hasPackage } from '@/checks/prerequisites.js';
import { selectTestRunner } from '@/checks/test-runner.js';
import type { PackageEcosystem } from '@/checks/prerequisites.js';
import type { DetectedStackProfile } from '@/core/types/introspection.js';
import type { StackPackTestRunner } from '@/core/types/pack.js';
import type { ProjectCommands, ProjectTesting } from '@/core/types/project-profile.js';

/** Pack ecosystem string → the lockfile ecosystem `hasPackage` understands (else null). */
const PACKAGE_ECOSYSTEMS: Record<string, PackageEcosystem> = {
  composer: 'composer',
  php: 'composer',
  python: 'python',
  node: 'node',
  javascript: 'node',
  typescript: 'node',
  ruby: 'ruby',
};

/** The ecosystem of the project's stack, for the lockfile lookup — the first pack that maps. */
export function resolvePackageEcosystem(
  frameworks: string[],
  projectRoot?: string,
): PackageEcosystem | null {
  for (const pack of getPacksForFrameworks(frameworks, projectRoot)) {
    const mapped = PACKAGE_ECOSYSTEMS[pack.manifest.ecosystem];
    if (mapped) return mapped;
  }
  /* v8 ignore next -- every shipped coding pack maps to a known ecosystem; the fallthrough is a guard */
  return null;
}

/** The lockfiles that back a `hasPackage` decision, for the drift hash. */
const ECOSYSTEM_LOCKFILES: Record<PackageEcosystem, string[]> = {
  composer: ['composer.lock'],
  python: ['poetry.lock', 'uv.lock', 'Pipfile.lock', 'pyproject.toml'],
  node: ['pnpm-lock.yaml', 'package-lock.json', 'yarn.lock', 'bun.lock'],
  ruby: ['Gemfile.lock'],
};

/**
 * A stable hash of the ecosystem's lockfile(s), so a later run re-derives the decision only when a
 * lockfile changed. Returns undefined when no lockfile is present (nothing to pin).
 */
export function lockfileHash(
  projectRoot: string,
  ecosystem: PackageEcosystem | null,
): string | undefined {
  if (!ecosystem) return undefined;
  const files = [...ECOSYSTEM_LOCKFILES[ecosystem]];
  if (ecosystem === 'python') {
    try {
      for (const entry of readdirSync(projectRoot)) {
        if (/^requirements.*\.txt$/i.test(entry)) files.push(entry);
      }
    } catch {
      /* v8 ignore next -- unreadable project root: fall through to whatever fixed files exist */
    }
  }
  const parts: string[] = [];
  for (const file of files.sort()) {
    const path = join(projectRoot, file);
    if (!existsSync(path)) continue;
    try {
      parts.push(`${file}\n${readFileSync(path, 'utf8')}`);
    } catch {
      /* v8 ignore next -- a present-but-unreadable lockfile contributes nothing to the hash */
    }
  }
  if (parts.length === 0) return undefined;
  return `sha256:${createHash('sha256').update(parts.join(' ')).digest('hex')}`;
}

/**
 * Insert a runner's parallel `flag` into the sequential test command, right after the runner
 * invocation and before its structured-output flags, so `<pattern>`/`--log-junit …` order is
 * preserved. When the structured flags are not in the command, the parallel flag is appended.
 */
export function insertParallelFlag(
  command: string,
  flag: string,
  structuredFlags: string | undefined,
): string {
  const sf = structuredFlags?.trim();
  if (sf && command.includes(sf)) {
    return command.replace(sf, `${flag} ${sf}`);
  }
  return `${command} ${flag}`;
}

export interface TestingDerivation {
  /** The parallel command — present only when `testing.parallel === 'available'`. */
  test_parallel?: string;
  testing: ProjectTesting;
}

/**
 * Derive the parallel-test record from a pack runner and the project's lockfile (issue #554, B.3).
 * `native` → no parallel command, mode `native`. `flag` + package present → a `test_parallel`
 * command, mode `available`. `flag` + package absent → `unavailable` with `<package>-missing`. No
 * lockfile or no parallel block → `unknown`. Fully deterministic; installs nothing.
 */
export function deriveTestingRecord(input: {
  runner: StackPackTestRunner;
  sequentialTest: string;
  projectRoot: string;
  ecosystem: PackageEcosystem | null;
  now: string;
  detectedBy?: 'script' | 'agent';
}): TestingDerivation {
  const { runner, sequentialTest, projectRoot, ecosystem, now } = input;
  const detected_by = input.detectedBy ?? 'script';
  const hash = lockfileHash(projectRoot, ecosystem);
  const base: Pick<ProjectTesting, 'runner_id' | 'detected_by' | 'recorded_at'> & {
    lockfile_hash?: string;
  } = {
    runner_id: runner.runner_id,
    detected_by,
    recorded_at: now,
    ...(hash ? { lockfile_hash: hash } : {}),
  };
  const parallel = runner.parallel;

  if (!parallel || parallel.mode === 'unavailable') {
    return {
      testing: {
        ...base,
        parallel: 'unavailable',
        reason: parallel?.reason ?? 'unknown',
      },
    };
  }

  if (parallel.mode === 'native') {
    return { testing: { ...base, parallel: 'native' } };
  }

  // mode === 'flag'
  const pkg = parallel.requires_package;
  const present = pkg && ecosystem ? hasPackage(projectRoot, ecosystem, pkg) : null;
  if (present === true && parallel.flag) {
    return {
      test_parallel: insertParallelFlag(sequentialTest, parallel.flag, runner.structured_flags),
      testing: { ...base, parallel: 'available' },
    };
  }
  if (present === false) {
    return { testing: { ...base, parallel: 'unavailable', reason: `${pkg}-missing` } };
  }
  return { testing: { ...base, parallel: 'unknown', reason: 'unknown' } };
}

/**
 * Derive `commands.test_parallel` + the `testing` block for a profile from the stack's selected
 * runner and the project lockfile (issue #554, B.3). Called at onboarding and re-run at `checks
 * run`. Writes the human stack-doc mirror too (skipped silently when the stack docs are absent), so
 * the machine record and the mirror always move together. Returns the (possibly augmented) commands
 * plus the testing block, or the commands unchanged when no structured runner resolves.
 */
export function deriveTestingForProfile(input: {
  stackProfile: Pick<DetectedStackProfile, 'frameworks' | 'traits'>;
  commands: ProjectCommands;
  projectRoot?: string;
  now: string;
  detectedBy?: 'script' | 'agent';
  /** Write the human stack-doc mirror. Default true; `checks plan` passes false to stay read-only. */
  mirrorDoc?: boolean;
}): { commands: ProjectCommands; testing?: ProjectTesting } {
  const { stackProfile, commands, projectRoot, now } = input;
  const runner = selectTestRunner(stackProfile, commands.test, projectRoot);
  if (!runner || !projectRoot) return { commands };

  const ecosystem = resolvePackageEcosystem(stackProfile.frameworks, projectRoot);
  const derived = deriveTestingRecord({
    runner,
    sequentialTest: commands.test,
    projectRoot,
    ecosystem,
    now,
    detectedBy: input.detectedBy,
  });
  const nextCommands = derived.test_parallel
    ? { ...commands, test_parallel: derived.test_parallel }
    : commands;
  if (derived.test_parallel && input.mirrorDoc !== false) {
    upsertStackDocCommandRow(projectRoot, 'Test (parallel)', derived.test_parallel);
  }
  return { commands: nextCommands, testing: derived.testing };
}

const STACK_DOC_REL = 'docs/instructions/stack/overview.md';
const STACK_DOC_HEADER = /^\|\s*action\s*\|\s*command\s*\|$/i;

/**
 * Mirror one recorded command into the stack doc's `| Action | Command |` table (issue #554, B.3):
 * replace the matching-action row or append it. When the file or the table is missing, append a
 * `## Test runner (recorded by paqad)` section with a two-row table. Skips silently when the
 * `docs/instructions/stack/` directory does not exist (a project without stack docs).
 */
export function upsertStackDocCommandRow(
  projectRoot: string,
  action: string,
  command: string,
): void {
  const docPath = join(projectRoot, STACK_DOC_REL);
  if (!existsSync(dirname(docPath))) return;

  const row = `| ${action} | ${command} |`;
  const rowRe = new RegExp(`^\\|\\s*${escapeRegExp(action)}\\s*\\|.*\\|\\s*$`, 'i');

  let content = existsSync(docPath) ? readFileSync(docPath, 'utf8') : '';
  const lines = content.split('\n');

  const headerIdx = lines.findIndex((line) => STACK_DOC_HEADER.test(line.trim()));
  if (headerIdx === -1) {
    const section = [
      '',
      '## Test runner (recorded by paqad)',
      '',
      '| Action | Command |',
      '|--------|---------|',
      row,
      '',
    ].join('\n');
    content = content.length > 0 ? `${content.replace(/\n*$/, '\n')}${section}` : `${section}`;
    writeFileSync(docPath, content.replace(/\n*$/, '\n'));
    return;
  }

  // Find the existing row for this action inside the table body and replace it; else append after
  // the table's last row.
  const existingIdx = lines.findIndex(
    (line, idx) => idx > headerIdx + 1 && rowRe.test(line.trim()),
  );
  if (existingIdx !== -1) {
    lines[existingIdx] = row;
  } else {
    let end = headerIdx + 1; // the separator row
    while (end + 1 < lines.length && lines[end + 1]!.trim().startsWith('|')) end += 1;
    lines.splice(end + 1, 0, row);
  }
  writeFileSync(docPath, lines.join('\n'));
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
